import { useEffect, useState } from "react";
import { useParams, Link } from "react-router-dom";
import { publicSnapshot } from "@/lib/api";
import LoadFactorChart from "@/components/LoadFactorChart";
import { MonthlyKwhChart, Co2TrendChart } from "@/components/MonthlyCharts";
import { PUBLIC } from "@/constants/testIds";
import { PartnerBanner } from "@/components/PartnerBanner";

export default function PublicSnapshot() {
  const { share_id } = useParams();
  const [data, setData] = useState(null);
  const [error, setError] = useState(null);

  useEffect(() => {
    (async () => {
      try {
        const d = await publicSnapshot(share_id);
        setData(d);
      } catch (e) {
        setError(e?.response?.data?.detail || "Snapshot not found");
      }
    })();
  }, [share_id]);

  if (error) {
    return (
      <div data-testid={PUBLIC.page} className="min-h-screen bg-[#F7F7F4] flex items-center justify-center px-6">
        <div className="text-center">
          <div className="overline mb-3">404</div>
          <h1 className="font-display text-4xl">{error}</h1>
          <Link to="/" className="mt-6 inline-block link-underline">Return to Kilowatt Ledger</Link>
        </div>
      </div>
    );
  }

  if (!data) {
    return (
      <div className="min-h-screen bg-[#F7F7F4] flex items-center justify-center">
        <div className="font-mono-data text-sm text-[#6E716C]">Loading snapshot…</div>
      </div>
    );
  }

  const { account_name, months = [], kpis = {} } = data;

  return (
    <div data-testid={PUBLIC.page} className="min-h-screen bg-[#F7F7F4]">
      <header className="border-b border-[#E5E5DE] bg-white">
        <div className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8 h-16 flex items-center justify-between">
          <Link to="/" className="flex items-center gap-3">
            <div className="w-7 h-7 bg-[#0F3524] flex items-center justify-center">
              <span className="font-mono-data text-[#F7F7F4] text-[10px]">KW</span>
            </div>
            <div className="font-display text-base tracking-tight">Kilowatt Ledger</div>
          </Link>
          <div className="text-xs font-mono-data text-[#6E716C] uppercase tracking-widest">Public snapshot</div>
        </div>
      </header>

      <div className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8 py-10 sm:py-16">
        <div className="mb-10 border-b border-[#E5E5DE] pb-6">
          <div className="overline mb-3">Editorial · Issue {kpis.months_tracked}</div>
          <h1 className="font-display text-4xl sm:text-5xl lg:text-6xl font-semibold tracking-tight">
            {account_name} — Sustainability Ledger
          </h1>
          <div className="mt-4 text-sm text-[#6E716C]">
            {kpis.months_tracked || 0} months tracked · Latest: {kpis.latest_month || "—"}
          </div>
        </div>

        {months.length === 0 ? (
          <div className="font-mono-data text-sm text-[#6E716C]">No bills yet.</div>
        ) : (
          <>
            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4 mb-8">
              <Kpi label="Total kWh" value={fmt(kpis.total_kwh, 0)} unit="kWh" />
              <Kpi label="Total cost" value={`RM ${fmt(kpis.total_cost_rm, 2)}`} />
              <Kpi label="Avg cost / kWh" value={`RM ${fmt(kpis.avg_cost_per_kwh, 4)}`} />
              <Kpi label="Total CO₂" value={fmt(kpis.total_co2_kg, 0)} unit="kg" />
            </div>

            <div className="mb-6">
              <LoadFactorChart months={months} />
            </div>
            <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
              <MonthlyKwhChart months={months} />
              <Co2TrendChart months={months} />
            </div>
          </>
        )}

        <div className="mt-16 bg-[#F7F7F4] text-[#1A1B1A] border-t border-[#E5E5DE] -mx-4 sm:-mx-6 lg:-mx-8 px-4 sm:px-6 lg:px-8 py-10 flex flex-col items-center gap-6 text-center">
          <div>
            <div className="text-[10px] font-bold uppercase tracking-[0.28em] text-[#B24F35] mb-2">Contact</div>
            <a
              href="mailto:alfred.initiatives@gmail.com"
              className="text-sm font-mono-data text-[#1A1B1A] hover:text-[#B24F35] transition-colors"
            >
              alfred.initiatives@gmail.com
            </a>
          </div>
          <PartnerBanner />
          <div className="text-xs text-[#6E716C] font-mono-data mt-1">
            Powered by <Link to="/" className="text-[#1A1B1A] link-underline">Kilowatt Ledger</Link> · Track your own footprint.
          </div>
        </div>
      </div>
    </div>
  );
}

function Kpi({ label, value, unit }) {
  return (
    <div className="flat-card p-6">
      <div className="overline">{label}</div>
      <div className="mt-4 flex items-baseline gap-2">
        <span className="font-mono-data text-3xl font-light tracking-tighter text-[#0F3524]">{value}</span>
        {unit && <span className="font-mono-data text-sm text-[#6E716C]">{unit}</span>}
      </div>
    </div>
  );
}

function fmt(n, digits = 2) {
  if (typeof n !== "number" || Number.isNaN(n)) return "—";
  return n.toLocaleString("en-MY", { minimumFractionDigits: digits, maximumFractionDigits: digits });
}
